import React, { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/ui/Modal';
import Select, { type SelectOption } from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import TextArea from '@/components/ui/TextArea';
import studiesService from '@/services/studies/StudiesService';
import type { Study } from '@/types';

interface StudyShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialStudyId?: number; // optional: preselect a study when opening
  phone?: string; // optional: target phone to send via WhatsApp
}

const StudyShareModal: React.FC<StudyShareModalProps> = ({ isOpen, onClose, initialStudyId, phone }) => {
  const [studyOptions, setStudyOptions] = useState<SelectOption[]>([]);
  const [selectedStudyId, setSelectedStudyId] = useState<string>('');
  const [studiesMap, setStudiesMap] = useState<Record<string, Study>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await studiesService.getStudies();
        if (!mounted) return;
        if (res.success) {
          const map: Record<string, Study> = {};
          const opts: SelectOption[] = (res.data || []).map((s) => {
            const id = String(s.id);
            map[id] = s as Study;
            return { value: id, label: s.title };
          });
          setStudiesMap(map);
          setStudyOptions(opts);
          // If an initialStudyId was provided, preselect it if present in the list
          if (initialStudyId && map[String(initialStudyId)]) {
            setSelectedStudyId(String(initialStudyId));
          } else {
            setSelectedStudyId('');
          }
        } else {
          setError(res.message || 'Não foi possível carregar os estudos');
        }
      } catch (e) {
        if (!mounted) return;
        setError('Erro ao carregar os estudos');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [isOpen, initialStudyId]);

  const selectedStudy = selectedStudyId ? studiesMap[selectedStudyId] : undefined;

  const shareText = useMemo(() => {
    if (!selectedStudy) return '';
    const title = selectedStudy.title?.trim() || 'Estudo Bíblico';
    const url = (selectedStudy as Partial<Study>).url?.trim();
    // Share only title and link as requested
    const lines = [
      'Olá! Gostaria de compartilhar um estudo bíblico com você.',
      '',
      `*${title}*`,
    ];
    if (url) {
      lines.push('', url);
    }
    return lines.join('\n');
  }, [selectedStudy]);

  const openWhatsApp = () => {
    const encoded = encodeURIComponent(shareText);
    const clean = phone ? phone.replace(/\D/g, '') : '';
    const url = clean
      ? `https://wa.me/${clean}?text=${encoded}`
      : `https://wa.me/?text=${encoded}`; // fallback: user chooses contact
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
    } catch {
      // noop
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Enviar estudo por WhatsApp" size="xl">
      <div className="space-y-4">
        <div>
          <Select
            label="Escolha o estudo"
            value={selectedStudyId}
            onChange={(e) => setSelectedStudyId(e.target.value)}
            options={studyOptions}
            placeholder={loading ? 'Carregando...' : 'Selecione um estudo'}
            disabled={loading}
            helperText={error || undefined}
            error={error || undefined}
          />
        </div>

        <div>
          <label className="form-label">Mensagem de pré-visualização</label>
          <TextArea
            value={shareText}
            onChange={() => { /* read-only preview */ }}
            rows={10}
            placeholder="Selecione um estudo para gerar a mensagem"
            readOnly
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={copyToClipboard} disabled={!selectedStudyId}>
            Copiar texto
          </Button>
          <Button onClick={openWhatsApp} disabled={!selectedStudyId}>
            Abrir no WhatsApp
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default StudyShareModal;
